import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/solid';

// The one gallery presentation shared by uploaded attachments (MediaModal) and
// camera recordings (CameraVideoGallery): header with title/date (plus a
// details row for video duration and caller metadata), the stage with
// previous/next arrows, the frame with counter and Download/Delete actions.
// Callers own loading, storage and deletion; the stage only renders their
// explicit state, so a caller error is always shown with Retry, never as an
// endless loader.

export const formatMediaDuration = seconds => {
  const total = Math.floor(seconds), h = Math.floor(total / 3600), m = Math.floor(total / 60) % 60, s = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

// Profile timezone where the caller has one; otherwise the established
// browser-local rendering.
export const formatGalleryDate = (value, timeZone) => new Date(value).toLocaleString(undefined, timeZone ? { timeZone } : undefined);

function GalleryVideo({ url, onDuration, onReady, onError }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const element = ref.current;
    // Effect replay runs cleanup and setup without a render in between. Restore
    // the declarative source that cleanup detached so the live setup never
    // keeps a source-less player.
    if (element.getAttribute('src') !== url) {
      element.setAttribute('src', url);
      element.load();
    }
    return () => { element.pause(); element.removeAttribute('src'); element.load(); };
  }, [url]);
  return <video ref={ref} src={url} controls autoPlay playsInline className="mm-media" aria-label="Video playback"
    onLoadedMetadata={event => {
      const { duration } = event.currentTarget;
      if (Number.isFinite(duration) && duration > 0) onDuration(duration);
    }} onLoadedData={() => onReady?.(url)} onError={() => onError?.(url)} />;
}

export default function MediaGalleryStage({
  title, date, timeZone, details = [], kind, url, alt, status, error, onRetry,
  index, count, canPrevious, canNext, onPrevious, onNext, onClose,
  onDownload, onDelete = null, busy = false, onVideoReady, onVideoError, notices = null, children = null,
}) {
  const [duration, setDuration] = useState(null);
  useEffect(() => { setDuration(null); }, [url]);
  const keys = useRef({});
  keys.current = { onPrevious, onNext, onClose };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') keys.current.onPrevious?.();
      else if (e.key === 'ArrowRight') keys.current.onNext?.();
      else if (e.key === 'Escape') keys.current.onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const shownDetails = details.filter(Boolean);
  const showDuration = kind === 'video' && status === 'ready' && duration !== null;

  return (
    <div className="media-overlay mm-shell">
      <div className="media-overlay-middle mm-stack">
        {notices}
        <header className="mm-header">
          <h3 className="mm-title">{title}</h3>
          <time className="mm-date">{formatGalleryDate(date, timeZone)}</time>
          {(shownDetails.length > 0 || showDuration) && <p className="mm-details">
            {shownDetails.map(detail => <span key={detail}>{detail}</span>)}
            {showDuration && <span className="mm-duration">{formatMediaDuration(duration)}</span>}
          </p>}
        </header>

        <div className="mm-stage">
          <button
            className="mm-arrow mm-arrow-left"
            onClick={(e) => { e.stopPropagation(); onPrevious(); }}
            disabled={busy || !canPrevious}
            aria-label="Previous"
          >
            &#8249;
          </button>
          <div className="mm-frame">
            {status === 'error' ? (
              <div className="mm-loading" role="alert">
                <span>{error}</span>
                {onRetry && <button type="button" className="mm-retry" onClick={(e) => { e.stopPropagation(); onRetry(); }}>Retry</button>}
              </div>
            ) : status !== 'ready' || !url ? (
              <div className="mm-loading">
                <div className="mm-spinner" aria-hidden="true" />
                <span>Loading media…</span>
              </div>
            ) : kind === 'image' ? (
              <img src={url} alt={alt} className="mm-media" />
            ) : (
              <GalleryVideo key={url} url={url} onDuration={setDuration} onReady={onVideoReady} onError={onVideoError} />
            )}

            {count > 0 && (
              <div className="mm-counter">{index + 1} / {count}</div>
            )}

            <div className="mm-floating-actions" onClick={(e) => e.stopPropagation()}>
              <button
                className="mm-icon-btn"
                onClick={(e) => { e.stopPropagation(); onDownload(); }}
                title="Download original"
                aria-label="Download original"
              >
                <ArrowDownTrayIcon />
              </button>
              {onDelete && (
                <button
                  className="mm-icon-btn mm-icon-btn-danger"
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  title="Delete"
                  aria-label="Delete"
                  disabled={busy}
                >
                  <TrashIcon />
                </button>
              )}
            </div>
          </div>
          <button
            className="mm-arrow mm-arrow-right"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            disabled={busy || !canNext}
            aria-label="Next"
          >
            &#8250;
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
